// Framework-agnostic data layer for the "Kontroll" (SupplierControls) panel.
//
// This module extracts the data fetching + field derivation logic from
// ts-components/app-add-supplier/supplier-controls.tsx so it can be reused
// (e.g. by a supplier comparison modal) without pulling in any JSX/React.
//
// The behavior here is intended to match supplier-controls.tsx exactly:
// same service calls, same arguments, same provider-fallback order and the
// same value formatting/math. Nothing in this file renders anything.

// Service imports copied from supplier-controls.tsx (paths unchanged: this
// file sits at the same depth — ts-components/<folder>/ — as the source).
import axios from 'axios';
import moment from 'moment';
import { ServiceSyna } from '../../services/service-syna';
import ServiceCollectiveAgreement from '../../services/service-collectiveAgreement';
import { ServiceId06 } from '../../services/service-id06';
import appState from '../../state/AppStateContainer';
import serviceSupplierMonitoring from '../../services/service-supplier-monitoring';
import { formatDate, formatAmount } from '../../util';
import serviceCredibility from '../../services/service-credibility';
import { isValidOrganizationNumber } from '../../utils/bt-number';
import serviceSanctions from '../../services/service-sanctions';

export type SupplierControlsFlags = {
  hasSyna: boolean;
  hasCreditsafe: boolean;
  hasTic: boolean;
};

/** Every remote source behind the Kontroll panel, for per-source error reporting. */
export type SupplierControlsSource =
  | 'syna'
  | 'creditsafe'
  | 'tic'
  | 'monitoring'
  | 'collectiveAgreement'
  | 'id06'
  | 'sanctions';

export type SupplierControlsRaw = {
  syna: any | null;
  creditsafe: any | null;
  tic: any | null;
  sanctions: { results: any[]; persons: any[]; status: any | null } | null;
  id06: any | null;
  collectiveAgreement: any | null;
  monitoring: any | null;
  paye: any | null;
  errors: Partial<Record<SupplierControlsSource, boolean>>;
};

/**
 * Fetches all "Kontroll" data for a single supplier, mirroring the two mount
 * effects in supplier-controls.tsx (the provider fetch effect ~181-238 and the
 * sanctions effect ~215-238) plus the get* handlers (~249-642).
 *
 * All provider calls run through Promise.allSettled so one failing provider
 * does not prevent the others from resolving.
 */
export async function fetchSupplierControls(
  organizationNumber: string,
  companyName: string | undefined,
  flags: SupplierControlsFlags,
  opts?: { creditsafeFresh?: boolean }
): Promise<SupplierControlsRaw> {
  const raw: SupplierControlsRaw = {
    syna: null,
    creditsafe: null,
    tic: null,
    sanctions: null,
    id06: null,
    collectiveAgreement: null,
    monitoring: null,
    paye: null,
    errors: {},
  };

  // TIC guard — mirrors getTicDetails (~621-627): only enabled for super-admin
  // orgs or orgs with hasTic, and only for a valid organization number.
  const ticEnabled =
    (appState.isSuperAdminOrg() || flags.hasTic) &&
    !!organizationNumber &&
    isValidOrganizationNumber(organizationNumber);

  // Sanctions args — mirrors the sanctions effect (~215-238). Skip entirely if
  // there is no company name; only pass the org number when a credit service
  // is available.
  const trimmedCompanyName = companyName?.trim();
  const sanctionsOrgNr = flags.hasSyna || flags.hasCreditsafe ? organizationNumber?.trim() : undefined;

  const [
    synaSettled,
    monitoringSettled,
    creditsafeSettled,
    ticSettled,
    agreementSettled,
    id06Settled,
    sanctionsSettled,
  ] = await Promise.allSettled([
    // Syna details (~309-318)
    flags.hasSyna ? ServiceSyna.getSynaDetails(organizationNumber) : Promise.resolve(null),
    // Supplier monitoring — only when hasSyna, exactly as getSynaDetails does (~319)
    flags.hasSyna
      ? serviceSupplierMonitoring.fetchMonitoredSupplier(organizationNumber)
      : Promise.resolve(null),
    // Creditsafe: cached by default, fresh/live endpoint on refresh (~568-615)
    flags.hasCreditsafe
      ? opts?.creditsafeFresh
        ? serviceCredibility.getCreditsafeDetails(organizationNumber)
        : serviceCredibility.getCachedCreditsafeDetails(organizationNumber)
      : Promise.resolve(null),
    // TIC (~621-642)
    ticEnabled
      ? serviceCredibility.getTicDetails(organizationNumber, flags.hasSyna || flags.hasCreditsafe)
      : Promise.resolve(null),
    // Collective agreement — unconditional (~200)
    ServiceCollectiveAgreement.getCollectiveAgreement(organizationNumber),
    // ID06 — unconditional (~210)
    ServiceId06.checkId06(organizationNumber),
    // Sanctions (~224)
    trimmedCompanyName ? serviceSanctions.search(trimmedCompanyName, sanctionsOrgNr) : Promise.resolve(null),
  ]);

  // ---- Map settled results ------------------------------------------------
  //
  // Each source resolves to its value or to null, recording an error flag when it REJECTED, so
  // callers can tell "had nothing to report" from "could not be reached".
  const settle = <T>(result: PromiseSettledResult<T>, source: SupplierControlsSource): T | null => {
    if (result.status === 'fulfilled') return result.value ?? null;
    raw.errors[source] = true;
    return null;
  };

  raw.syna = settle(synaSettled, 'syna');
  // Syna also reports failure in-band, resolving with an { error } payload instead of rejecting.
  if (raw.syna?.error) raw.errors.syna = true;
  raw.monitoring = settle(monitoringSettled, 'monitoring');
  raw.creditsafe = settle(creditsafeSettled, 'creditsafe');

  if (ticSettled.status === 'fulfilled') {
    raw.tic = ticSettled.value ?? null;
  } else if (!(axios.isAxiosError(ticSettled.reason) && [400, 503].includes(ticSettled.reason.response?.status ?? 0))) {
    raw.errors.tic = true;
  }

  raw.collectiveAgreement = settle(agreementSettled, 'collectiveAgreement');

  // ServiceId06.checkId06 returns the raw axios response; the component reads
  // `.data` (~210-211).
  raw.id06 = settle(id06Settled, 'id06')?.data ?? null;

  const sanctions = settle(sanctionsSettled, 'sanctions');
  if (sanctions) {
    raw.sanctions = {
      results: sanctions.results || [],
      persons: sanctions.personResult?.persons || [],
      status: sanctions.status || null,
    };
  }

  raw.paye = resolvePaye(raw, flags);

  return raw;
}

/**
 * Resolve the merged PAYE value from the provider slots. Same precedence as every other merged
 * field: Syna > Creditsafe > TIC — see deriveFskatt / deriveMoms / deriveArbetsgivare below, and
 * the panel's own merged block, commented "priority: Syna > Creditsafe > Tic". Split out so
 * mergeSupplierControls can recompute it after slots change.
 */
function resolvePaye(raw: SupplierControlsRaw, flags: SupplierControlsFlags): any | null {
  const arbgivavg = hasSynaData(raw, flags) ? raw.syna.arbgivavg : null;
  if (arbgivavg?.amount && arbgivavg.year && arbgivavg.month) {
    return { amount: arbgivavg.amount, period: new Date(arbgivavg.year, arbgivavg.month - 1) };
  }
  if (hasCreditsafeData(raw, flags) && raw.creditsafe.paye) return raw.creditsafe.paye;
  if (raw.tic?.paye) return raw.tic.paye;
  return null;
}

/**
 * Merge a fresh fetch over the previous one, per source: when the new fetch failed a source and
 * the previous fetch had data for it, keep the previous slot — a partial outage during refresh
 * must not wipe values the user is looking at. The error flag stays set, so the UI still names
 * the failed source (whose retained values may now be outdated) and withholds the "Uppdaterad"
 * stamp. Mirrors the economics side, which serves cached data flagged refreshFailed instead of
 * dropping it when a refresh fails.
 */
export function mergeSupplierControls(
  prev: SupplierControlsRaw | null | undefined,
  next: SupplierControlsRaw,
  flags: SupplierControlsFlags
): SupplierControlsRaw {
  const retained = (Object.keys(next.errors) as SupplierControlsSource[]).filter(
    source => next.errors[source] && prev && !prev.errors[source] && prev[source] !== null
  );
  if (!retained.length) return next;
  const merged = { ...next };
  for (const source of retained) {
    // Source names are also the slot keys; TS cannot correlate the union key across the two
    // indexed accesses, hence the cast.
    (merged as any)[source] = prev![source];
  }
  merged.paye = resolvePaye(merged, flags);
  return merged;
}

function hasSynaData(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean {
  return !!(flags.hasSyna && r.syna && !r.syna.error);
}

function hasCreditsafeData(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean {
  return !!(flags.hasCreditsafe && r.creditsafe && Object.keys(r.creditsafe).length > 0);
}

function hasTicData(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean {
  return !!(flags.hasTic && r.tic);
}

function baseFieldsVisible(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean {
  return hasSynaData(r, flags) || hasCreditsafeData(r, flags) || hasTicData(r, flags);
}

// ---------------------------------------------------------------------------
// Pure derivation helpers — primitives only, no JSX. Each reproduces the
// component's exact fallback order and formatting.
// ---------------------------------------------------------------------------

/** F-skatt (~484-492). Priority: Syna > Creditsafe > Tic. Null when no provider has data. */
export function deriveFskatt(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean | null {
  if (!baseFieldsVisible(r, flags)) return null;
  if (hasSynaData(r, flags)) return !!r.syna.fskatt;
  if (hasCreditsafeData(r, flags)) return !!r.creditsafe.registeredForFTax;
  if (hasTicData(r, flags)) return !!r.tic.registeredForFTax;
  return false;
}

/** Reg. för moms / VAT (~495-503). Priority: Syna > Creditsafe > Tic. */
export function deriveMoms(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean | null {
  if (!baseFieldsVisible(r, flags)) return null;
  if (hasSynaData(r, flags)) return !!r.syna.momsreg;
  if (hasCreditsafeData(r, flags)) return !!r.creditsafe.registeredForVat;
  if (hasTicData(r, flags)) return !!r.tic.registeredForVat;
  return false;
}

/** Reg. arbetsgivare / PAYE registered (~506-514). Priority: Syna > Creditsafe > Tic. */
export function deriveArbetsgivare(r: SupplierControlsRaw, flags: SupplierControlsFlags): boolean | null {
  if (!baseFieldsVisible(r, flags)) return null;
  if (hasSynaData(r, flags)) return !!r.syna.arbetsgivarreg;
  if (hasCreditsafeData(r, flags)) return !!r.creditsafe.registeredForEmployeeTax;
  if (hasTicData(r, flags)) return !!r.tic.registeredForEmployeeTax;
  return false;
}

/** Skatteanstånd / tax deferral (~541-548). Syna > Tic (no Creditsafe). Formatted "{amount} kr". */
export function deriveSkatteanstand(r: SupplierControlsRaw, flags: SupplierControlsFlags): string | null {
  if (!baseFieldsVisible(r, flags)) return null;
  if (hasSynaData(r, flags) && r.syna.postponedTax?.[0]?.amount) {
    return `${(+r.syna.postponedTax[0].amount).toLocaleString()} kr`;
  }
  if (hasTicData(r, flags) && r.tic.postponedTax) {
    return `${formatAmount(r.tic.postponedTax)} kr`;
  }
  return '–';
}

/**
 * Kollektivavtal / collective agreement boolean (collectiveAgreement.jsx
 * ~15-28). true when the summed agreement counts are > 0, false when the data
 * is present but empty, null when unknown/error. The comparison table shows a
 * plain Ja/Nej flag for this — a static info icon next to the row label
 * explains what the data source IS generically (see deriveKollektivavtalDetails
 * below for the per-supplier specifics, shown on hovering the flag itself).
 */
export function deriveKollektivavtal(r: SupplierControlsRaw): boolean | null {
  if (r.errors.collectiveAgreement) return null;
  const a = r.collectiveAgreement;
  if (!a) return null;
  const kollektivavtalskollen = a.kollektivavtalskollen?.source_data?.length ?? 0;
  const collectiveAgreements = a.byggInstSektorn?.collectiveAgreements?.length ?? 0;
  const substituteAgreements = a.byggInstSektorn?.substituteAgreements?.length ?? 0;
  return kollektivavtalskollen + collectiveAgreements + substituteAgreements > 0;
}

/**
 * The specific agreements a supplier has — same data CollectiveAccordion.jsx renders in the
 * single-supplier "Visa avtal" drawer (renderSource/renderAgreement, ~4-18, and the
 * byggInstSektorn table ~54-74), reshaped into one flat row list so it can be shown compactly in
 * a hover tooltip instead of requiring that drawer.
 */
export function deriveKollektivavtalDetails(r: SupplierControlsRaw): {
  rows: { source: string; agreement: string; employer: string }[];
  memberships: string[];
} {
  const a = r.collectiveAgreement;
  const rows: { source: string; agreement: string; employer: string }[] = [];
  if (!a) return { rows, memberships: [] };

  // Kollektivavtalskollen: one row per (source, agreement) pair.
  for (const source of a.kollektivavtalskollen?.source_data ?? []) {
    for (const ag of source?.agreements ?? []) {
      rows.push({ source: source?.source_name ?? '', agreement: ag?.agreement ?? '', employer: ag?.arbetsgivarorganisation ?? '' });
    }
  }

  // Bygg- och installationssektorn: collective agreements, then substitute agreements
  // (the drawer labels the employer column "(hängavtal)" for substitutes, not a real org name).
  const byggInst = a.byggInstSektorn;
  for (const c of byggInst?.collectiveAgreements ?? []) {
    rows.push({ source: c?.FackligMotpart ?? '', agreement: c?.Avtal ?? '', employer: c?.Medlemsorganisation ?? '' });
  }
  for (const c of byggInst?.substituteAgreements ?? []) {
    rows.push({ source: c?.FackligMotpart ?? '', agreement: c?.Avtal ?? '', employer: '(hängavtal)' });
  }

  const memberships = (byggInst?.memberships ?? []).map((m: any) => m?.name).filter(Boolean);
  return { rows, memberships };
}

/** Sanktionslistor / sanctions (renderSanctions ~644-727). */
export function deriveSanctions(r: SupplierControlsRaw): {
  label: 'clear' | 'match' | 'unknown' | 'refreshing';
  matches: number;
  names: string[];
} {
  if (!r.sanctions || r.errors.sanctions) {
    return { label: 'unknown', matches: 0, names: [] };
  }
  // Mirrors the component's isBackendLoading (~647-648): the backend's sanctions list is itself
  // mid-refresh, so any results already returned may be stale — surface that ahead of a plain
  // clear/match read rather than showing a possibly-stale "Förekommer inte" during the sweep.
  if (r.sanctions.status?.loading && !r.sanctions.status?.ready) {
    return { label: 'refreshing', matches: 0, names: [] };
  }
  const results = r.sanctions.results || [];
  const persons = r.sanctions.persons || [];
  const totalMatches = results.length + persons.length;
  const names = persons.map((p: any) => p.personName);
  return { label: totalMatches > 0 ? 'match' : 'clear', matches: totalMatches, names };
}

/** ID06 (~890-904). */
export function deriveId06(r: SupplierControlsRaw): {
  status: boolean | null;
  cards: number | null;
  error: boolean;
} {
  if (!r.id06) return { status: null, cards: null, error: false };
  if (r.id06.error) return { status: null, cards: null, error: true };
  return { status: !!r.id06.status, cards: r.id06.cards ?? 0, error: false };
}

/** Arbetsgivaravgifter (~905-909). "{formatAmount(amount)} kr (MMMM YYYY)". */
export function deriveArbetsgivaravgifter(r: SupplierControlsRaw): string | null {
  if (!r.paye) return null;
  const period = moment(r.paye.period).locale('sv').format('MMMM YYYY');
  return `${formatAmount(r.paye.amount)} kr (${period})`;
}

/** Antal heltidsanställda (uppskattat) (~915). Math.ceil(amount / 0.3142 / 30000). */
export function deriveHeltidEstimate(r: SupplierControlsRaw): number | null {
  if (!r.paye) return null;
  return Math.ceil(r.paye.amount / 0.3142 / 30000);
}

/**
 * Bevakad / monitored
 *
 * `monitored` is null when the monitoring lookup itself failed. The absence of a record proves
 * nothing in that case, and "false" is an affirmative claim — the supplier IS not monitored —
 * which is exactly the reading the data doesn't support. The component has the same flaw: its
 * fetch has no catch at all, so a failure leaves the state undefined and renders "Nej".
 */
export function deriveBevakad(
  r: SupplierControlsRaw,
  flags: SupplierControlsFlags
): { monitored: boolean | null; since: string | null; lastEvent: string | null } {
  if (!(flags.hasSyna || flags.hasCreditsafe)) {
    return { monitored: false, since: null, lastEvent: null };
  }
  if (r.errors.monitoring) return { monitored: null, since: null, lastEvent: null };
  const m = r.monitoring;
  return {
    monitored: !!m,
    since: m ? formatDate(m.monitoredSince) : null,
    lastEvent: m?.lastEventDate ? formatDate(m.lastEventDate) : null,
  };
}
